import { useState, useEffect } from 'react';
import { testimonialAPI, uploadAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Plus, Pencil, Trash2, Star, Upload, Loader2, Image as ImageIcon,
  GripVertical, User, Quote
} from 'lucide-react';
import { toast } from 'sonner';

const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'SMA FAVORIT',
  'BUMN'
];

const DEFAULT_TESTIMONIAL = {
  name: '',
  role: '',
  institution: '',
  image_url: '',
  quote: '',
  rating: 5,
  program: '',
  is_active: true,
  sort_order: 0
};

export function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_TESTIMONIAL);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const res = await testimonialAPI.getAll();
      setTestimonials(res.data || []);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
      toast.error('Gagal memuat testimoni');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTestimonial(null);
    setFormData({ ...DEFAULT_TESTIMONIAL, sort_order: testimonials.length });
    setDialogOpen(true);
  };

  const openEditDialog = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name || '',
      role: testimonial.role || '',
      institution: testimonial.institution || '',
      image_url: testimonial.image_url || '',
      quote: testimonial.quote || '',
      rating: testimonial.rating || 5,
      program: testimonial.program || '',
      is_active: testimonial.is_active !== false,
      sort_order: testimonial.sort_order || 0
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Hanya file gambar yang diizinkan');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }

    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const res = await uploadAPI.uploadFile(uploadFormData);
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Foto berhasil diupload');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Gagal mengupload foto');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Nama harus diisi');
      return;
    }
    if (!formData.role.trim()) {
      toast.error('Role/Jabatan harus diisi');
      return;
    }
    if (!formData.quote.trim()) {
      toast.error('Testimoni harus diisi');
      return;
    }

    try {
      setSaving(true);
      
      if (editingTestimonial) {
        await testimonialAPI.update(editingTestimonial.id, formData);
        toast.success('Testimoni berhasil diperbarui');
      } else {
        await testimonialAPI.create(formData);
        toast.success('Testimoni berhasil ditambahkan');
      }
      
      setDialogOpen(false);
      loadTestimonials();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Gagal menyimpan testimoni');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus testimoni ini?')) return;
    
    try {
      await testimonialAPI.delete(id);
      toast.success('Testimoni berhasil dihapus');
      loadTestimonials();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Gagal menghapus testimoni');
    }
  };

  const toggleActive = async (testimonial) => {
    try {
      await testimonialAPI.update(testimonial.id, { is_active: !testimonial.is_active });
      toast.success(testimonial.is_active ? 'Testimoni dinonaktifkan' : 'Testimoni diaktifkan');
      loadTestimonials();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4472C4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="testimonials-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Manajemen Testimoni</h2>
          <p className="text-sm text-slate-500">Kelola testimoni alumni yang ditampilkan di landing page</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="add-testimonial-btn">
          <Plus className="w-4 h-4 mr-2" />
          Tambah Testimoni
        </Button>
      </div>

      {/* Testimonials Grid */}
      {testimonials.length === 0 ? (
        <Card data-testid="empty-testimonials">
          <CardContent className="py-12 text-center">
            <Quote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Belum ada testimoni</p>
            <Button onClick={openCreateDialog} variant="outline" className="mt-4" data-testid="add-first-testimonial-btn">
              <Plus className="w-4 h-4 mr-2" />
              Tambah Testimoni Pertama
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-grid">
          {testimonials.map((t, idx) => (
            <Card key={t.id} className={`relative ${!t.is_active ? 'opacity-60' : ''}`} data-testid={`testimonial-card-${idx}`}>
              <CardContent className="p-4">
                {/* Status Badge */}
                <div className="absolute top-2 right-2 flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`testimonial-status-${idx}`}>
                    {t.is_active ? 'Aktif' : 'Nonaktif'}
                  </span>
                </div>

                {/* Profile */}
                <div className="flex items-start gap-3 mb-3">
                  {t.image_url ? (
                    <img 
                      src={t.image_url} 
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                      data-testid={`testimonial-image-${idx}`}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center" data-testid={`testimonial-placeholder-${idx}`}>
                      <User className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate" data-testid={`testimonial-name-${idx}`}>{t.name}</p>
                    <p className="text-xs text-[#4472C4] truncate" data-testid={`testimonial-role-${idx}`}>{t.role}</p>
                    <p className="text-xs text-slate-400 truncate" data-testid={`testimonial-institution-${idx}`}>{t.institution}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-3 h-3 ${i < t.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} 
                    />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-sm text-slate-600 line-clamp-3 mb-3" data-testid={`testimonial-quote-${idx}`}>&ldquo;{t.quote}&rdquo;</p>

                {/* Program Badge */}
                {t.program && (
                  <span className="inline-block text-xs px-2 py-0.5 bg-[#4472C4]/10 text-[#4472C4] rounded mb-3" data-testid={`testimonial-program-${idx}`}>
                    {t.program}
                  </span>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openEditDialog(t)}
                      className="h-8 px-2"
                      data-testid={`edit-testimonial-${idx}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(t.id)}
                      className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-testimonial-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Switch 
                    checked={t.is_active} 
                    onCheckedChange={() => toggleActive(t)}
                    data-testid={`toggle-testimonial-${idx}`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title">
              {editingTestimonial ? 'Edit Testimoni' : 'Tambah Testimoni Baru'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Image Upload */}
            <div>
              <Label>Foto Profil</Label>
              <div className="mt-2 flex items-center gap-4">
                {formData.image_url ? (
                  <img 
                    src={formData.image_url} 
                    alt="Preview"
                    className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                    data-testid="image-preview"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-slate-300" />
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="hidden"
                    id="image-upload"
                    data-testid="image-upload-input"
                  />
                  <label htmlFor="image-upload">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      disabled={uploading}
                      asChild
                    >
                      <span data-testid="upload-photo-btn">
                        {uploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploading ? 'Mengupload...' : 'Upload Foto'}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-400 mt-1">Max 2MB, format JPG/PNG</p>
                </div>
              </div>
              {/* Manual URL input as fallback */}
              <Input
                placeholder="Atau masukkan URL gambar..."
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                className="mt-2"
                data-testid="image-url-input"
              />
            </div>

            {/* Name */}
            <div>
              <Label>Nama Lengkap *</Label>
              <Input
                placeholder="Contoh: Ahmad Rizky"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="name-input"
              />
            </div>

            {/* Role */}
            <div>
              <Label>Role/Status Kelulusan *</Label>
              <Input
                placeholder="Contoh: Lulus UTBK-SNBT 2025"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                data-testid="role-input"
              />
            </div>

            {/* Institution */}
            <div>
              <Label>Institusi/Universitas</Label>
              <Input
                placeholder="Contoh: Universitas Indonesia"
                value={formData.institution}
                onChange={(e) => setFormData(prev => ({ ...prev, institution: e.target.value }))}
                data-testid="institution-input"
              />
            </div>

            {/* Program */}
            <div>
              <Label>Program</Label>
              <Select
                value={formData.program || '__none__'}
                onValueChange={(v) => setFormData(prev => ({ ...prev, program: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger data-testid="program-select">
                  <SelectValue placeholder="Pilih program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">- Tidak ada -</SelectItem>
                  {PROGRAMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quote */}
            <div>
              <Label>Testimoni *</Label>
              <Textarea
                placeholder="Tuliskan testimoni alumni..."
                value={formData.quote}
                onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.value }))}
                rows={4}
                data-testid="quote-input"
              />
            </div>

            {/* Rating */}
            <div>
              <Label>Rating</Label>
              <div className="flex items-center gap-1 mt-2" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                    className="p-1 hover:scale-110 transition-transform"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star 
                      className={`w-6 h-6 ${star <= formData.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} 
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Order */}
            <div>
              <Label>Urutan Tampil</Label>
              <Input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                data-testid="sort-order-input"
              />
              <p className="text-xs text-slate-400 mt-1">Angka kecil tampil lebih dulu</p>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Status Aktif</Label>
                <p className="text-xs text-slate-400">Tampilkan di landing page</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                data-testid="active-status-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-btn">
              Batal
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#4472C4] hover:bg-[#3861a8]"
              data-testid="save-testimonial-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {editingTestimonial ? 'Simpan Perubahan' : 'Tambah Testimoni'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TestimonialsTab;
